import React, { FC, SVGProps, useState } from 'react';
import { Button, ButtonStyles, Icon, IconButton, Icons } from '@silverfort/sf-components';
import { Policy, PolicyTemplate } from '../../../../types/policies';
import { PolicyConfiguration } from '../../types/PolicyConfiguration';
import { useHistory } from 'react-router-dom';
import { convertFormValuesToPolicy, getActionDisclaimer } from '../utils/forms';
import FormLayout from './FormLayout/FormLayout';
import { usePolicyForm } from '../hooks/usePolicyForm';
import TemplateDetails from './TemplateDetails';
import { POLICY_ACTION_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { useHandleStartPolicyFromTemplate } from '../hooks/useHandleStartPolicyFromTemplate';

interface TopBannerProps {
    actionDisclaimer: string;
    open: boolean;
    onClose: () => void;
}

interface FooterProps {
    createDisabled?: boolean;
    onCancelClick: () => void;
    onCreateClick: () => void;
}

interface Props {
    template: PolicyTemplate;
    policyConfiguration: PolicyConfiguration;
    newPolicyData?: Policy;
}

const TopBanner: FC<TopBannerProps> = ({ actionDisclaimer, open, onClose }) => (
    <>
        {open && (
            <div className="p-4 bg-sf-primary-50 border-b border-solid border-sf-primary-500 w-full flex justify-between items-center">
                <div className="flex flex-row gap-1">
                    <Icon
                        icon={Icons.CircleInfo as FC<SVGProps<SVGSVGElement>>}
                        size={16}
                        className="text-sf-primary min-w-4 min-h-4"
                    />
                    <span className="font-sf text-12 font-medium leading-[14.65px]">{actionDisclaimer}</span>
                </div>
                <IconButton iconSize={16} icon={Icons.Cancel as FC<SVGProps<SVGSVGElement>>} onClick={onClose} />
            </div>
        )}
    </>
);

const Footer: FC<FooterProps> = ({ createDisabled = false, onCancelClick, onCreateClick }) => {
    return (
        <div className="h-[56px] border-t border-sf-gray-100 px-6 gap-2 flex justify-end items-center">
            <Button buttonStyle={ButtonStyles.GhostSolid} text="Cancel" onClick={onCancelClick} />
            <Button disabled={createDisabled} text="Review Policy" onClick={onCreateClick} />
        </div>
    );
};

const shouldShowActionTopBanner = (template: PolicyTemplate) =>
    [POLICY_ACTION_TYPES.NOTIFY.value, POLICY_ACTION_TYPES.MFA.value].every((action) =>
        template.supportedActions.includes(action)
    );

const CreateFromTemplateForm: FC<Props> = ({ template, policyConfiguration, newPolicyData }) => {
    const history = useHistory();
    const { navigateToCreatePolicyFromTemplate } = useHandleStartPolicyFromTemplate();
    const [actionBannerOpen, setActionBannerOpen] = useState(shouldShowActionTopBanner(template));
    const { policyFormValues, fieldErrors, formValid, setPolicyFormValue } = usePolicyForm(
        template,
        policyConfiguration
    );

    const handleCloseActionBanner = () => {
        setActionBannerOpen(false);
    };

    const handleCancelClick = () => {
        history.goBack();
    };

    const handleCreateClick = () => {
        navigateToCreatePolicyFromTemplate(template.id, convertFormValuesToPolicy(policyFormValues));
    };

    return (
        <div className="h-full w-full flex flex-col">
            <TopBanner
                actionDisclaimer={actionBannerOpen ? getActionDisclaimer(template) : ''}
                open={actionBannerOpen}
                onClose={handleCloseActionBanner}
            />
            <div className="pt-10 ps-10 pe-10 w-full flex-1 flex flex-col gap-8 overflow-y-scroll">
                <TemplateDetails
                    name={template.name}
                    isFavorite={template.isFavorite}
                    description={template.description}
                    type={template.typeId}
                    spns={template.spns}
                    authTypes={template.authTypes}
                    protocols={template.supportedProtocols}
                    risks={template.risks}
                    disclaimer={template.disclaimer}
                    hideSpnChips={template.hideSpnChips ?? false}
                />
                <FormLayout
                    template={template}
                    policyFormValues={policyFormValues}
                    setPolicyFormValue={setPolicyFormValue}
                    policyConfiguration={policyConfiguration}
                    newPolicyData={newPolicyData}
                    fieldErrors={fieldErrors}
                />
            </div>
            <Footer createDisabled={!formValid} onCancelClick={handleCancelClick} onCreateClick={handleCreateClick} />
        </div>
    );
};

export default CreateFromTemplateForm;
